import { useState, useEffect, useCallback } from 'react';
import { getUserData } from '../utils/cookies';
import { User, UseUserReturn } from '../types';

/**
 * Hook to get the current user data.
 * Uses data stored in cookies from login - no API call needed.
 */
export function useUser(): UseUserReturn {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUser = useCallback(() => {
    setLoading(true);
    setError(null);
    try {
      const storedUser = getUserData();
      if (storedUser) {
        // Convert StoredUserData to User format
        const userData: User = {
          id: storedUser.id,
          username: storedUser.username,
          firstName: storedUser.firstName,
          lastName: storedUser.lastName,
          roles: storedUser.roles,
        };
        setUser(userData);
      } else {
        setUser(null);
        setError('No user data found');
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('Failed to load user data');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Wrap loadUser in async to match the expected signature
  const refetch = useCallback(async () => {
    loadUser();
  }, [loadUser]);

  return { user, loading, error, refetch };
}

export default useUser;
